import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { PaymentForm, CreditCard, ApplePay, GooglePay } from "react-square-web-payments-sdk";
import { useCart } from "../context/CartContext";
import { useAuth } from "../context/AuthContext";
import { api, formatApiErrorDetail } from "../lib/api";
import { Lock, Zap } from "lucide-react";

const SQUARE_APP_ID = process.env.REACT_APP_SQUARE_APP_ID;
const SQUARE_LOCATION_ID = process.env.REACT_APP_SQUARE_LOCATION_ID;

export default function Checkout() {
  const { items, subtotal, shipping, hasPhysical, lineKey, clear } = useCart();
  const { user } = useAuth();
  const navigate = useNavigate();

  const [form, setForm] = useState({
    full_name: user?.name || "",
    email: user?.email || "",
    address: "", city: "", zip: "",
  });
  const [promoCode, setPromoCode] = useState("");
  const [appliedPromo, setAppliedPromo] = useState(null);
  const [promoError, setPromoError] = useState("");
  const [promoChecking, setPromoChecking] = useState(false);
  const [paying, setPaying] = useState(false);
  const [error, setError] = useState("");
  const [rush, setRush] = useState(false);
  const [rushQuote, setRushQuote] = useState(null);
  const [taxQuote, setTaxQuote] = useState(null);

  // Quote rush fee whenever ZIP changes
  useEffect(() => {
    if (!hasPhysical) { setRushQuote(null); return; }
    const z = (form.zip || "").trim();
    if (!z) { setRushQuote(null); return; }
    if (z.length < 5) return;
    let cancelled = false;
    api.get("/shipping/rush-quote", { params: { zip: z.slice(0, 5) } })
      .then(({ data }) => { if (!cancelled) setRushQuote(data); })
      .catch(() => { /* keep last quote */ });
    return () => { cancelled = true; };
  }, [form.zip, hasPhysical]);

  const discount = appliedPromo ? Number((subtotal * appliedPromo.percent_off / 100).toFixed(2)) : 0;
  const rushFee = rush && hasPhysical && rushQuote ? rushQuote.fee : 0;
  const taxableBase = Number((subtotal - discount + shipping + rushFee).toFixed(2));

  // Quote sales tax whenever ZIP, email or taxable base changes (debounced to avoid hammering)
  useEffect(() => {
    const z = (form.zip || "").trim();
    if (!z || z.length < 5 || taxableBase <= 0) { setTaxQuote(null); return; }
    let cancelled = false;
    const t = setTimeout(() => {
      api.get("/checkout/tax-quote", {
        params: { zip: z.slice(0, 5), amount: taxableBase, email: form.email || "" },
      })
        .then(({ data }) => { if (!cancelled) setTaxQuote(data); })
        .catch(() => { /* keep last quote */ });
    }, 350);
    return () => { cancelled = true; clearTimeout(t); };
  }, [form.zip, form.email, taxableBase]);

  const taxAmount = taxQuote?.amount || 0;
  const taxLabel = taxQuote?.label || null;
  const total = Math.max(0, Number((taxableBase + taxAmount).toFixed(2)));
  const totalCents = Math.round(total * 100);

  if (items.length === 0) {
    return (
      <div className="max-w-2xl mx-auto px-6 py-24 text-center" data-testid="checkout-empty">
        <div className="font-script text-3xl text-[#e8478e]">Your Bag Is Empty</div>
        <h1 className="font-display text-4xl font-black text-[#1c1815] tracking-tight">Nothing to check out</h1>
        <button onClick={() => navigate("/shop")} className="mt-6 bg-[#2eb6a8] hover:bg-[#1f8d83] text-white font-bold uppercase tracking-[0.2em] text-xs px-6 py-3.5 rounded-full">Shop Now</button>
      </div>
    );
  }

  // Square Web Payments SDK calls this after a successful tokenize
  const handlePaymentToken = async (token, verifiedBuyer) => {
    setError("");
    setPaying(true);
    try {
      const payload = {
        source_id: token.token,
        verification_token: verifiedBuyer?.token || null,
        full_name: form.full_name,
        email: form.email,
        address: form.address,
        city: form.city,
        zip: form.zip,
        items: items.map((i) => ({
          product_id: i.product_id,
          quantity: i.quantity,
          variant_size: i.variant_size || null,
          color: i.color || null,
        })),
        promo_code: appliedPromo ? appliedPromo.code : null,
        rush: !!rush,
      };
      const { data } = await api.post("/checkout/square-payment", payload);
      if (data?.order_id) {
        clear();
        navigate(`/order/${data.order_id}`, { replace: true });
      } else {
        throw new Error("Payment succeeded but no order was created. Please contact support.");
      }
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message || "Payment failed");
      setPaying(false);
    }
  };

  const validateBuyerInfo = () => {
    if (!form.full_name.trim()) return "Please enter your full name";
    if (!form.email.trim()) return "Please enter your email";
    if (hasPhysical) {
      if (!form.address.trim()) return "Please enter your shipping address";
      if (!form.city.trim()) return "Please enter your city";
      if (!form.zip.trim()) return "Please enter your ZIP";
    }
    return null;
  };

  const tryPromo = async () => {
    setPromoError("");
    if (!promoCode.trim()) return;
    setPromoChecking(true);
    try {
      const { data } = await api.post("/promo/validate", { code: promoCode.trim(), email: form.email });
      setAppliedPromo(data);
      setPromoError("");
    } catch (err) {
      setAppliedPromo(null);
      setPromoError(formatApiErrorDetail(err.response?.data?.detail) || "Invalid code");
    } finally { setPromoChecking(false); }
  };
  const removePromo = () => { setAppliedPromo(null); setPromoCode(""); setPromoError(""); };

  const formIncomplete = !!validateBuyerInfo();

  return (
    <div data-testid="checkout-page" className="max-w-6xl mx-auto px-6 py-12 sm:py-16">
      <div className="font-script text-3xl text-[#2eb6a8]">Final Step, Babe</div>
      <h1 className="font-display text-5xl font-black text-[#1c1815] tracking-tight">Checkout</h1>

      {!user && (
        <div
          data-testid="checkout-signin-prompt"
          className="mt-6 flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-4 rounded-2xl border-2 border-[#e8478e]/40 bg-[#e8478e]/8"
        >
          <div className="text-sm text-[#1c1815] leading-snug">
            <span className="font-bold uppercase tracking-[0.18em] text-[10px] text-[#c93875] block mb-1">Already a babe?</span>
            <span>Sign in to save this cart, track your order, and get <strong>20% off your next order $50+</strong>.</span>
          </div>
          <div className="flex gap-2 flex-shrink-0">
            <button
              type="button"
              onClick={() => navigate("/login?next=" + encodeURIComponent("/checkout"))}
              data-testid="checkout-login-btn"
              className="bg-[#1c1815] hover:bg-[#3a2f25] text-white text-[11px] font-bold uppercase tracking-[0.18em] px-5 py-2.5 rounded-full transition-all whitespace-nowrap"
            >
              Sign In
            </button>
            <button
              type="button"
              onClick={() => navigate("/register?next=" + encodeURIComponent("/checkout"))}
              data-testid="checkout-register-btn"
              className="bg-[#e8478e] hover:bg-[#c93875] text-white text-[11px] font-bold uppercase tracking-[0.18em] px-5 py-2.5 rounded-full transition-all whitespace-nowrap"
            >
              Create Account
            </button>
          </div>
        </div>
      )}

      <div className="mt-10 grid grid-cols-1 lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2 space-y-5 p-7 sm:p-9 rounded-3xl border border-[#e5dccd] bg-white shadow-sm">
          <h2 className="font-display font-extrabold text-2xl text-[#1c1815]">Contact & Shipping</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field label="Full name" testid="co-name" value={form.full_name} onChange={(v) => setForm({ ...form, full_name: v })} required/>
            <Field label="Email" type="email" testid="co-email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} required/>
          </div>
          <Field label="Address" testid="co-address" value={form.address} onChange={(v) => setForm({ ...form, address: v })}/>
          <div className="grid grid-cols-2 gap-4">
            <Field label="City" testid="co-city" value={form.city} onChange={(v) => setForm({ ...form, city: v })}/>
            <Field label="ZIP / Postal" testid="co-zip" value={form.zip} onChange={(v) => setForm({ ...form, zip: v })}/>
          </div>

          <div className="mt-2 p-4 rounded-2xl border border-[#2eb6a8]/40 bg-[#2eb6a8]/8 text-sm text-[#4a4239]">
            <Lock size={14} className="inline text-[#2eb6a8] mr-1.5"/>
            <span className="font-bold uppercase tracking-[0.2em] text-[10px] text-[#1f8d83]">Secure Square Checkout</span> — Pay with Apple Pay, Google Pay, or any major card. Sandbox test card <span className="font-mono text-[#1c1815]">4111 1111 1111 1111</span>, any future date, any CVV.
          </div>

          {formIncomplete && (
            <div data-testid="form-incomplete-notice" className="text-[#8a8076] text-xs">
              Fill in your contact{hasPhysical ? " and shipping" : ""} info above to enable payment.
            </div>
          )}

          {error && <div data-testid="checkout-error" className="text-[#c93875] text-sm font-bold">{error}</div>}

          {!formIncomplete && totalCents > 0 && (
            <div data-testid="square-payment-block" className="mt-2">
              <h3 className="font-display font-extrabold text-xl text-[#1c1815] mb-3">Payment Method</h3>
              <PaymentForm
                applicationId={SQUARE_APP_ID}
                locationId={SQUARE_LOCATION_ID}
                cardTokenizeResponseReceived={handlePaymentToken}
                createPaymentRequest={() => ({
                  countryCode: "US",
                  currencyCode: "USD",
                  total: {
                    amount: total.toFixed(2),
                    label: "Snip Happens Boutique",
                  },
                })}
              >
                <div className="space-y-3">
                  <div data-testid="apple-pay-button">
                    <ApplePay />
                  </div>
                  <div data-testid="google-pay-button">
                    <GooglePay buttonColor="black" buttonType="long" />
                  </div>
                  <div className="relative my-3">
                    <div className="absolute inset-0 flex items-center">
                      <div className="w-full border-t border-[#e5dccd]"></div>
                    </div>
                    <div className="relative flex justify-center">
                      <span className="bg-white px-3 text-[10px] uppercase tracking-[0.25em] text-[#8a8076] font-bold">Or Pay With Card</span>
                    </div>
                  </div>
                  <div data-testid="square-card-form">
                    <CreditCard
                      buttonProps={{
                        css: {
                          backgroundColor: "#e8478e",
                          fontSize: "12px",
                          fontWeight: "700",
                          letterSpacing: "0.2em",
                          textTransform: "uppercase",
                          color: "white",
                          "&:hover": { backgroundColor: "#c93875" },
                        },
                      }}
                    >
                      {paying ? "Processing…" : `Pay $${total.toFixed(2)}`}
                    </CreditCard>
                  </div>
                </div>
              </PaymentForm>
              {paying && (
                <div data-testid="payment-processing" className="mt-3 text-center text-sm text-[#1f8d83] font-bold">
                  Processing your payment…
                </div>
              )}
            </div>
          )}
        </div>

        <aside className="p-6 rounded-3xl border border-[#e5dccd] bg-white shadow-sm h-fit sticky top-28">
          <h3 className="font-display font-extrabold text-xl text-[#1c1815] mb-4">Order Summary</h3>
          <div className="space-y-3 max-h-72 overflow-y-auto pr-2" data-testid="checkout-items">
            {items.map((it) => {
              const k = lineKey(it);
              return (
              <div key={k} className="flex gap-3" data-testid={`checkout-item-${k}`}>
                <img src={it.image_url} alt="" className="w-14 h-14 object-cover rounded-xl"/>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-bold text-[#1c1815] line-clamp-2">{it.title}</div>
                  <div className="text-xs text-[#8a8076]">
                    {it.variant_size && <span className="text-[#1f8d83] font-bold">Size {it.variant_size}</span>}
                    {it.variant_size && it.color && <span className="mx-1">·</span>}
                    {it.color && <span className="text-[#1f8d83] font-bold">{it.color}</span>}
                    {(it.variant_size || it.color) && <span className="mx-1">·</span>}
                    Qty {it.quantity}
                  </div>
                </div>
                <div className="text-sm font-bold text-[#1c1815]">${(it.price * it.quantity).toFixed(2)}</div>
              </div>
              );
            })}
          </div>

          {hasPhysical && (
            <div className="mt-4 text-[11px] text-[#1f8d83] bg-[#2eb6a8]/10 border border-[#2eb6a8]/30 rounded-xl px-3 py-2.5 leading-relaxed" data-testid="processing-notice">
              Each item is handmade to order with care. Please allow <strong>3–5 business days</strong> for processing before shipment.
            </div>
          )}

          <div className="border-t border-[#e5dccd] mt-5 pt-5">
            <div className="text-[10px] uppercase tracking-[0.25em] text-[#8a8076] font-bold mb-2">Promo Code</div>
            {appliedPromo ? (
              <div data-testid="applied-promo" className="flex items-center justify-between gap-2 bg-[#e8478e]/10 border border-[#e8478e]/40 rounded-xl px-3 py-2">
                <div className="text-sm">
                  <span className="font-mono text-[#c93875] font-bold">{appliedPromo.code}</span>
                  <span className="text-[#4a4239] ml-2 text-xs">−{appliedPromo.percent_off}%</span>
                </div>
                <button type="button" onClick={removePromo} className="text-[10px] text-[#8a8076] hover:text-[#e8478e] uppercase tracking-[0.2em] font-bold">Remove</button>
              </div>
            ) : (
              <div className="flex gap-2">
                <input type="text" data-testid="promo-input" value={promoCode} onChange={(e) => setPromoCode(e.target.value.toUpperCase())} placeholder="SNIPHAPPENS10"
                  className="flex-1 bg-[#faf4e9] border border-[#e5dccd] focus:border-[#2eb6a8] rounded-xl px-3 py-2 text-sm outline-none uppercase text-[#1c1815]"/>
                <button type="button" onClick={tryPromo} disabled={promoChecking || !promoCode.trim()} data-testid="apply-promo"
                  className="text-[10px] font-bold uppercase tracking-[0.2em] bg-[#2eb6a8] hover:bg-[#1f8d83] text-white px-4 rounded-xl transition-all disabled:opacity-50">
                  {promoChecking ? "…" : "Apply"}
                </button>
              </div>
            )}
            {promoError && <div data-testid="promo-error" className="text-[#c93875] text-xs mt-2">{promoError}</div>}
          </div>

          <div className="border-t border-[#e5dccd] mt-5 pt-5 space-y-2 text-sm">
            <Row label="Subtotal" value={`$${subtotal.toFixed(2)}`}/>
            {discount > 0 && <Row label={`Discount (${appliedPromo.code})`} value={`−$${discount.toFixed(2)}`} accent/>}
            <Row
              label={hasPhysical ? "Shipping" : "Shipping (digital)"}
              value={shipping > 0 ? `$${shipping.toFixed(2)}` : "Free"}
              muted={shipping === 0}
              testid="checkout-shipping"
            />
            {hasPhysical && shipping > 0 && (
              <div className="text-[11px] text-[#8a8076] -mt-1">Add ${(50 - subtotal).toFixed(2)} more for free shipping ✨</div>
            )}

            {hasPhysical && (
              <div className="mt-3 p-3 rounded-xl border-2 border-[#e5dccd] bg-[#faf4e9] hover:border-[#e8478e]/40 transition-all" data-testid="rush-block">
                <label className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    data-testid="rush-toggle"
                    checked={rush}
                    onChange={(e) => setRush(e.target.checked)}
                    className="mt-1 w-4 h-4 accent-[#e8478e]"
                  />
                  <div className="flex-1">
                    <div className="flex items-center gap-1.5 font-bold text-[#1c1815]">
                      <Zap size={14} className="text-[#e8478e]" fill="currentColor"/> Rush my order
                    </div>
                    <div className="text-[11px] text-[#4a4239] mt-0.5">
                      Skip the line — we bump your order to the front of the queue.
                    </div>
                    {!form.zip && (
                      <div className="text-[11px] text-[#8a8076] mt-1">Enter your ZIP to see your rush price</div>
                    )}
                    {form.zip && rushQuote && (
                      <div className="text-[11px] mt-1">
                        <span className="text-[#1f8d83] font-bold">{rushQuote.label}: </span>
                        <span className="text-[#1c1815] font-bold">+${rushQuote.fee.toFixed(2)}</span>
                      </div>
                    )}
                  </div>
                </label>
              </div>
            )}

            {rush && rushFee > 0 && (
              <Row
                label={rushQuote?.label || "Rush delivery"}
                value={`+$${rushFee.toFixed(2)}`}
                accent
                testid="checkout-rush"
              />
            )}

            {taxAmount > 0 && (
              <Row
                label={taxLabel || "Sales tax"}
                value={`$${taxAmount.toFixed(2)}`}
                testid="checkout-tax"
              />
            )}

            <div className="flex justify-between mt-3 pt-3 border-t border-[#e5dccd]">
              <span className="font-display font-extrabold text-[#1c1815]">Total</span>
              <span data-testid="checkout-total" className="font-display font-extrabold text-2xl text-[#1c1815]">${total.toFixed(2)}</span>
            </div>
          </div>
        </aside>
      </div>
    </div>
  );
}

function Field({ label, value, onChange, type = "text", testid, required }) {
  return (
    <div>
      <label className="text-[10px] uppercase tracking-[0.25em] text-[#8a8076] font-bold">{label}</label>
      <input type={type} required={required} value={value} data-testid={testid} onChange={(e) => onChange(e.target.value)}
        className="mt-1.5 w-full bg-[#faf4e9] border border-[#e5dccd] focus:border-[#2eb6a8] focus:ring-2 focus:ring-[#2eb6a8]/20 rounded-xl px-4 py-3 outline-none text-[#1c1815]"/>
    </div>
  );
}
function Row({ label, value, muted, accent, testid }) {
  return (
    <div className="flex justify-between" data-testid={testid}>
      <span className={muted ? "text-[#8a8076]" : accent ? "text-[#c93875]" : "text-[#4a4239]"}>{label}</span>
      <span className={muted ? "text-[#8a8076]" : accent ? "text-[#c93875] font-bold" : "text-[#1c1815]"}>{value}</span>
    </div>
  );
}
